import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ScannerScanView from './ScannerScanView';
import ScannerHistoryView from './ScannerHistoryView';
import ScannerProfileView from './ScannerProfileView';
import { useSchoolName } from './useSchoolName';
import './ScannerDashboard.css';

function ScannerDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [scannerData, setScannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const schoolName = useSchoolName(scannerData?.schoolId);

  const tabs = [
    { id: 0, title: 'Scan', icon: '📍' },
    { id: 1, title: 'History', icon: '📋' },
    { id: 2, title: 'Profile', icon: '👤' },
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};

          let scannerName = userData.username || userData.fullName || user.displayName;

          if (!scannerName) {
            const scannerDoc = await getDoc(doc(db, 'scanners', user.uid));
            const scannerRecord = scannerDoc.exists() ? scannerDoc.data() : {};
            scannerName = scannerRecord.fullName || scannerRecord.name || user.email.split('@')[0];
          }

          setScannerData({
            uid: user.uid,
            email: user.email,
            displayName: scannerName || user.email.split('@')[0],
            role: 'scanner',
            schoolId: userData.schoolId || null,
          });
        } catch (error) {
          console.error('Error loading scanner data:', error);
          setScannerData({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0],
            role: 'scanner',
            schoolId: null,
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="scanner-dashboard">
      <header className="dashboard-header">
        <div className="header-brand">
          <img src="/logo512.png" alt="KN Education Portal logo" className="header-logo" />
          <div>
            <h1>KN Education Portal</h1>
            {schoolName && <span className="header-school">{schoolName}</span>}
          </div>
        </div>
        <div className="header-actions">
          <span className="scanner-name">{scannerData?.displayName || 'Scanner'}</span>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-layout">
        <nav className="sidebar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`nav-button ${selectedTab === tab.id ? 'active' : ''}`}
              onClick={() => setSelectedTab(tab.id)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.title}</span>
            </button>
          ))}
        </nav>

        <main className="main-content">
          {selectedTab === 0 && <ScannerScanView scannerData={scannerData} />}
          {selectedTab === 1 && <ScannerHistoryView scannerData={scannerData} />}
          {selectedTab === 2 && <ScannerProfileView scannerData={scannerData} schoolName={schoolName} onLogout={handleLogout} />}
        </main>
      </div>

      {/* Mobile-only content area — outside dashboard-layout to avoid flex height collapse */}
      <main className="mobile-main-content">
        {selectedTab === 0 && <ScannerScanView scannerData={scannerData} />}
        {selectedTab === 1 && <ScannerHistoryView scannerData={scannerData} />}
        {selectedTab === 2 && <ScannerProfileView scannerData={scannerData} schoolName={schoolName} onLogout={handleLogout} />}
      </main>

      {/* Mobile bottom nav */}
      <nav className="mobile-bottom-nav">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`mobile-nav-btn ${selectedTab === tab.id ? 'active' : ''}`}
            onClick={() => setSelectedTab(tab.id)}
          >
            <span className="mobile-nav-icon">{tab.icon}</span>
            <span className="mobile-nav-label">{tab.title}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default ScannerDashboard;
